import { addDays, format, isSameDay, startOfDay } from "date-fns";

// Equipment ETA (demo): 2–4 business hours from now (in 15-min increments).
// Business hours are 8:00 AM – 5:00 PM; the ETA rolls into the next business
// day when there aren't enough business hours left today.
export interface EquipmentEta {
  etaMs: number;
  tomorrow: boolean;
}

const BIZ_START_MIN = 8 * 60; // 8:00 AM
const BIZ_END_MIN = 17 * 60; // 5:00 PM
const ETA_SLICE_MS = 15 * 60 * 1000;

const roundUp15 = (minutes: number): number => Math.ceil(minutes / 15) * 15;

export const makeEquipmentEta = (now: Date): EquipmentEta => {
  // 2–4 business hours from now, in 15-minute increments.
  const minutesToAdd = (8 + Math.floor(Math.random() * 9)) * 15; // 120–240 min

  // Start at now (rounded up to the next 15) clamped into the business day.
  let dayOffset = 0;
  let minOfDay = roundUp15(now.getHours() * 60 + now.getMinutes());
  if (minOfDay < BIZ_START_MIN) {
    minOfDay = BIZ_START_MIN;
  } else if (minOfDay >= BIZ_END_MIN) {
    dayOffset = 1;
    minOfDay = BIZ_START_MIN;
  }

  // Advance through business hours, rolling to the next day when one runs out.
  let remaining = minutesToAdd;
  while (remaining > 0) {
    const availableToday = BIZ_END_MIN - minOfDay;
    if (remaining <= availableToday) {
      minOfDay += remaining;
      remaining = 0;
    } else {
      remaining -= availableToday;
      dayOffset += 1;
      minOfDay = BIZ_START_MIN;
    }
  }

  const d = startOfDay(addDays(now, dayOffset));
  d.setHours(Math.floor(minOfDay / 60), minOfDay % 60, 0, 0);
  return { etaMs: d.getTime(), tomorrow: dayOffset > 0 };
};

export const formatEquipmentEta = (eta: EquipmentEta): string => {
  const t = format(new Date(eta.etaMs), "h:mm a");
  return eta.tomorrow ? `Tomorrow ${t}` : t;
};

// Re-roll an ETA: only today's ETAs shift — 20% earlier, 20% later, 60%
// unchanged — staying within business hours and the future.
export const refreshEquipmentEtaMs = (etaMs: number): number => {
  const now = new Date();
  if (!isSameDay(new Date(etaMs), now)) {
    return etaMs;
  }
  const roll = Math.random();
  const deltaMs = roll < 0.2 ? -ETA_SLICE_MS : roll < 0.4 ? ETA_SLICE_MS : 0;
  if (deltaMs === 0) {
    return etaMs;
  }
  const newMs = etaMs + deltaMs;
  const d = new Date(newMs);
  const newMin = d.getHours() * 60 + d.getMinutes();
  if (deltaMs < 0 && (newMs <= now.getTime() || newMin < BIZ_START_MIN)) {
    return etaMs;
  }
  if (deltaMs > 0 && newMin > BIZ_END_MIN) {
    return etaMs;
  }
  return newMs;
};
